/* S1 NBL BOGO Builder — interaction port of NBLProductSection.tsx over
   server-rendered product data (no storefront fetches):

   - gender toggle (mens / womens / kids) + womens category tabs; switching
     replaces the ACTIVE slot's product with the new list's first style
     unless that slot is already locked in
   - style thumbnails set the active slot's product (size resets)
   - two fixed slots: picking a size locks a slot in; locking slot 1
     auto-advances to slot 2 pre-loaded with the same style; slot 2 stays
     locked until slot 1 is done
   - gallery mirrors the active slot's product (arrows, swipe, full-screen)
   - ATC adds both variants through the theme's _s1 cart plumbing (same
     validate-and-fallback shape as s1-pdp.js) — no prices shown by design */

const ICONS = {
  check: '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>',
  x: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>',
  plus: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="M12 5v14"/></svg>',
  lock: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>',
  chevronDown: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>',
};

const SIZE_SHORT = { Small: 'S', Medium: 'M', Large: 'L' };

class S1NblBogo extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    try {
      this.groups = JSON.parse(this.querySelector('[data-bogo-data]').textContent);
    } catch (err) {
      console.error('s1-nbl-bogo: bad data payload', err);
      return;
    }

    this.state = {
      gender: 'mens',
      category: 'womens_a',
      activeSlot: 0,
      galleryIndex: 0,
      loading: false,
      slots: [
        { product: null, sizeLabel: null, variantId: null, added: false },
        { product: null, sizeLabel: null, variantId: null, added: false },
      ],
    };

    const list = this.currentList();
    if (list.length) this.state.slots[0].product = list[0];

    this.el = {
      track: this.querySelector('[data-bogo-track]'),
      galleryWrap: this.querySelector('[data-bogo-gallery-wrap]'),
      styles: this.querySelector('[data-bogo-styles]'),
      stylesWrap: this.querySelector('[data-bogo-styles-wrap]'),
      slots: this.querySelector('[data-bogo-slots]'),
      cats: this.querySelector('[data-bogo-cats]'),
      progress: this.querySelector('[data-bogo-progress]'),
      atc: this.querySelector('[data-bogo-atc]'),
      error: this.querySelector('[data-bogo-error]'),
      zoom: this.querySelector('[data-bogo-zoom]'),
      zoomImg: this.querySelector('[data-bogo-zoom-img]'),
    };
    this._atcLabel = this.el.atc.textContent;

    this.bindStatic();
    this.render();
  }

  currentList() {
    const key = this.state.gender === 'womens' ? this.state.category : this.state.gender;
    return this.groups[key] || [];
  }

  activeProduct() {
    return this.state.slots[this.state.activeSlot].product;
  }

  bindStatic() {
    /* gender + womens category toggles */
    this.querySelectorAll('[data-bogo-gender]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (this.state.gender === btn.dataset.bogoGender) return;
        this.state.gender = btn.dataset.bogoGender;
        this.onListChange();
      });
    });
    this.querySelectorAll('[data-bogo-cat]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (this.state.category === btn.dataset.bogoCat) return;
        this.state.category = btn.dataset.bogoCat;
        this.onListChange();
      });
    });

    /* gallery: arrows, click-to-zoom, swipe */
    this.querySelectorAll('[data-bogo-nav]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.stepGallery(Number(btn.dataset.bogoNav));
      });
    });
    this.el.galleryWrap.addEventListener('click', () => {
      const images = this.activeProduct() ? this.activeProduct().images : [];
      if (!images.length) return;
      this.el.zoomImg.src = images[this.state.galleryIndex];
      this.el.zoom.hidden = false;
    });
    this.querySelector('[data-bogo-zoom-close]').addEventListener('click', () => {
      this.el.zoom.hidden = true;
      this.el.zoomImg.removeAttribute('src');
    });
    let touchStart = null;
    this.el.galleryWrap.addEventListener('touchstart', (e) => {
      touchStart = e.targetTouches[0].clientX;
    }, { passive: true });
    this.el.galleryWrap.addEventListener('touchend', (e) => {
      if (touchStart === null) return;
      const distance = touchStart - e.changedTouches[0].clientX;
      if (distance > 50) this.stepGallery(1);
      if (distance < -50) this.stepGallery(-1);
      touchStart = null;
    }, { passive: true });

    /* thumbnail rail scroll arrows */
    this.querySelectorAll('[data-bogo-styles-nav]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.el.styles.scrollBy({ left: 200 * Number(btn.dataset.bogoStylesNav), behavior: 'smooth' });
      });
    });
    this.el.styles.addEventListener('scroll', () => this.updateStyleArrows());
    window.addEventListener('resize', () => this.updateStyleArrows());

    /* copy code */
    this.querySelectorAll('[data-bogo-copy]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (navigator.clipboard) navigator.clipboard.writeText(btn.dataset.bogoCopy).catch(() => {});
      });
    });

    this.el.atc.addEventListener('click', () => this.addToCart());
  }

  onListChange() {
    const list = this.currentList();
    const slot = this.state.slots[this.state.activeSlot];
    if (!slot.added && list.length) {
      this.state.slots[this.state.activeSlot] = { product: list[0], sizeLabel: null, variantId: null, added: false };
      this.state.galleryIndex = 0;
    }
    this.render();
  }

  setProduct(handle) {
    const product = this.currentList().find((p) => p.handle === handle);
    if (!product) return;
    this.state.slots[this.state.activeSlot] = { product, sizeLabel: null, variantId: null, added: false };
    this.state.galleryIndex = 0;
    this.render();
  }

  setSize(slotIdx, sizeLabel) {
    const slot = this.state.slots[slotIdx];
    if (!slot.product) return;
    const size = slot.product.sizes.find((s) => s.label === sizeLabel && s.available);
    if (!size) return;
    slot.sizeLabel = size.label;
    slot.variantId = size.id;
    slot.added = true;
    /* auto-advance: finishing slot 1 opens slot 2 pre-loaded with the same
       style (NBLProductSection handleSizeChange) */
    if (slotIdx === 0 && !this.state.slots[1].added) {
      this.state.activeSlot = 1;
      if (!this.state.slots[1].product) {
        this.state.slots[1] = { product: slot.product, sizeLabel: null, variantId: null, added: false };
      }
      this.state.galleryIndex = 0;
    }
    this.render();
  }

  removeSlot(slotIdx) {
    const slot = this.state.slots[slotIdx];
    slot.added = false;
    slot.sizeLabel = null;
    slot.variantId = null;
    this.state.activeSlot = slotIdx;
    this.state.galleryIndex = 0;
    this.render();
  }

  setActiveSlot(slotIdx) {
    if (slotIdx === 1 && !this.state.slots[0].added) return;
    if (this.state.activeSlot === slotIdx) return;
    this.state.activeSlot = slotIdx;
    this.state.galleryIndex = 0;
    this.render();
  }

  stepGallery(dir) {
    const images = this.activeProduct() ? this.activeProduct().images : [];
    if (images.length < 2) return;
    this.state.galleryIndex = (this.state.galleryIndex + dir + images.length) % images.length;
    this.renderGalleryPosition();
    if (!this.el.zoom.hidden) this.el.zoomImg.src = images[this.state.galleryIndex];
  }

  /* ---- rendering ---- */

  render() {
    const { gender, activeSlot, slots } = this.state;

    this.querySelectorAll('[data-bogo-gender]').forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.bogoGender === gender);
    });
    this.el.cats.hidden = gender !== 'womens';
    this.querySelectorAll('[data-bogo-cat]').forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.bogoCat === this.state.category);
    });
    this.querySelectorAll('[data-bogo-usps]').forEach((box) => {
      box.hidden = (box.dataset.bogoUsps === 'womens') !== (gender === 'womens');
    });

    this.renderGallery();
    this.renderStyles();
    this.renderSlots();

    this.el.progress.textContent = `${activeSlot + 1}/2`;
    this.querySelectorAll('[data-bogo-bar]').forEach((bar) => {
      bar.classList.toggle('is-added', slots[Number(bar.dataset.bogoBar)].added);
    });

    const ready = slots.every((s) => s.added) && !this.state.loading;
    this.el.atc.disabled = !ready;
    this.el.atc.classList.toggle('is-ready', ready);
    this.el.atc.textContent = this.state.loading ? 'Adding...' : this._atcLabel;
  }

  renderGallery() {
    const product = this.activeProduct();
    const images = product ? product.images : [];
    this.el.track.innerHTML = images
      .map((src, i) => `<img src="${src}" alt="" loading="${i === 0 ? 'eager' : 'lazy'}">`)
      .join('');
    this.querySelectorAll('[data-bogo-nav]').forEach((btn) => {
      btn.hidden = images.length < 2;
    });
    if (this.state.galleryIndex >= images.length) this.state.galleryIndex = 0;
    this.renderGalleryPosition();
  }

  renderGalleryPosition() {
    this.el.track.style.transform = `translateX(-${this.state.galleryIndex * 100}%)`;
  }

  renderStyles() {
    const current = this.activeProduct();
    this.el.styles.innerHTML = this.currentList()
      .map((p) => {
        const selected = current && current.handle === p.handle;
        return `
          <div class="s1-bogo__style">
            <button type="button" class="s1-bogo__style-btn${selected ? ' is-selected' : ''}" data-bogo-style="${p.handle}">
              <img src="${p.images[0] || ''}" alt="${p.label}" loading="lazy">
              ${selected ? `<span class="s1-bogo__style-check"><span>${ICONS.check}</span></span>` : ''}
            </button>
            <span class="s1-bogo__style-label">${p.label}</span>
          </div>`;
      })
      .join('');
    this.el.styles.querySelectorAll('[data-bogo-style]').forEach((btn) => {
      btn.addEventListener('click', () => this.setProduct(btn.dataset.bogoStyle));
    });
    this.updateStyleArrows();
  }

  updateStyleArrows() {
    const track = this.el.styles;
    const prev = this.querySelector('[data-bogo-styles-nav="-1"]');
    const next = this.querySelector('[data-bogo-styles-nav="1"]');
    if (!prev || !next) return;
    prev.hidden = track.scrollLeft <= 0;
    next.hidden = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
  }

  renderSlots() {
    const { slots, activeSlot } = this.state;
    this.el.slots.innerHTML = slots
      .map((slot, i) => {
        const locked = i === 1 && !slots[0].added;
        const cardClasses = [
          's1-bogo__slot-card',
          slot.added ? 'is-added' : '',
          !slot.added && activeSlot === i ? 'is-active' : '',
          locked ? 'is-locked' : '',
        ].filter(Boolean).join(' ');
        const showSelect = slot.product && (i === 0 || slots[0].added);
        return `
          <div class="s1-bogo__slot">
            <div class="${cardClasses}" data-bogo-slot="${i}">
              ${slot.product
                ? `<img src="${slot.product.images[0] || ''}" alt="${slot.product.label}">
                   <button type="button" class="s1-bogo__slot-remove" data-bogo-remove="${i}" title="Remove product">${ICONS.x}</button>`
                : `<div class="s1-bogo__slot-empty">${ICONS.plus}</div>`}
              ${slot.added ? `<span class="s1-bogo__slot-added">${ICONS.check}</span>` : ''}
              ${locked ? `<div class="s1-bogo__slot-lock">${ICONS.lock}</div>` : ''}
            </div>
            ${showSelect
              ? `<div class="s1-bogo__slot-size">
                   <select data-bogo-size="${i}" aria-label="Select size">
                     <option value="" disabled${slot.sizeLabel ? '' : ' selected'}>Select Size</option>
                     ${this.sizeOptions(slot)}
                   </select>
                   <span class="s1-bogo__slot-size-check">${ICONS.check}</span>
                   <span class="s1-bogo__slot-size-chev">${ICONS.chevronDown}</span>
                 </div>`
              : ''}
          </div>`;
      })
      .join('');

    this.el.slots.querySelectorAll('[data-bogo-slot]').forEach((card) => {
      card.addEventListener('click', () => this.setActiveSlot(Number(card.dataset.bogoSlot)));
    });
    this.el.slots.querySelectorAll('[data-bogo-remove]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.removeSlot(Number(btn.dataset.bogoRemove));
      });
    });
    this.el.slots.querySelectorAll('[data-bogo-size]').forEach((select) => {
      select.addEventListener('change', () => this.setSize(Number(select.dataset.bogoSize), select.value));
    });
  }

  sizeOptions(slot) {
    /* one option per size value; a size counts available if ANY variant is */
    const seen = new Map();
    slot.product.sizes.forEach((s) => {
      if (!seen.has(s.label)) seen.set(s.label, s.available);
      else if (s.available) seen.set(s.label, true);
    });
    return [...seen.entries()]
      .map(([label, available]) => {
        const selected = slot.sizeLabel === label ? ' selected' : '';
        const disabled = available ? '' : ' disabled';
        return `<option value="${label}"${selected}${disabled}>${SIZE_SHORT[label] || label}${available ? '' : ' (OOS)'}</option>`;
      })
      .join('');
  }

  /* ---- cart ---- */

  async addToCart() {
    const items = [];
    for (const slot of this.state.slots) {
      if (!slot.added || !slot.variantId) return;
      const existing = items.find((i) => i.id === slot.variantId);
      if (existing) existing.quantity += 1;
      else items.push({ id: slot.variantId, quantity: 1 });
    }
    this.state.loading = true;
    this.el.error.hidden = true;
    this.render();

    const directAdd = async () => {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        let description = '';
        try {
          const body = await res.json();
          description = body.description || body.message || '';
        } catch (parseErr) { /* non-JSON error body */ }
        const err = new Error(`cart/add ${res.status}`);
        err.userMessage = description;
        throw err;
      }
      return res.json();
    };

    try {
      let usedDirect = false;
      if (window._s1 && window._s1.addToCart && window._s1.refreshCart) {
        /* same validate-between-steps shape as s1-pdp.js: _s1.addToCart
           resolves Shopify error bodies as success, so check for their
           numeric status before refreshing */
        let body = null;
        try {
          body = await window._s1.addToCart(items);
        } catch (err) {
          console.error('s1-nbl-bogo: _s1 add failed, falling back', err);
          await directAdd();
          usedDirect = true;
        }
        if (!usedDirect) {
          if (body && typeof body.status === 'number' && body.status >= 400) {
            const err = new Error(`cart/add ${body.status}`);
            err.userMessage = body.description || body.message || '';
            throw err;
          }
          try {
            await window._s1.refreshCart(body);
          } catch (err) {
            console.error('s1-nbl-bogo: cart refresh failed after add', err);
            usedDirect = true;
          }
        }
      } else {
        await directAdd();
        usedDirect = true;
      }
      if (usedDirect) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:change'));
      }
    } catch (err) {
      console.error('s1-nbl-bogo: add to cart failed', err, items);
      this.el.error.textContent = err.userMessage || 'Could not add to cart. Please try again.';
      this.el.error.hidden = false;
    } finally {
      this.state.loading = false;
      this.render();
    }
  }
}

if (!customElements.get('s1-nbl-bogo')) {
  customElements.define('s1-nbl-bogo', S1NblBogo);
}
